import { useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { categories, articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const article = useMemo(() => articles.find((a) => a.id === Number(id)), [id]);
  const category = useMemo(() => (article ? categories.find((c) => c.slug === article.category) : undefined), [article]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.id !== article.id && a.category === article.category)
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-foreground-500 mb-4">文章不存在或已被删除</p>
          <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm font-medium">返回首页</Link>
        </div>
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '无翼鸟漫画',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://wingless-bird-manga.readdy.ai/article/${article.id}`,
    },
  };

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={`${article.tags.join(',')},${category?.name || ''},无翼鸟漫画`}
        canonicalUrl={`/article/${article.id}`}
        ogType="article"
        ogImage={article.image}
        structuredData={{
          ...articleSchema,
          '@context': 'https://schema.org',
        }}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Article Header */}
        <section className="pt-24 pb-8 md:pt-28 md:pb-10 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '分类浏览', to: '/category/tongren' },
                { label: category?.name || '', to: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
            <h1 className="text-xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mt-4 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-user-line"></i> {article.author}
              </span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-calendar-line"></i> {article.date}
              </span>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <i className="ri-eye-line"></i> {article.views.toLocaleString()} 次阅读
              </span>
              {category && (
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-0.5 rounded bg-accent-100 text-accent-700 text-xs font-medium whitespace-nowrap hover:bg-accent-200 transition-colors"
                >
                  {category.name}
                </Link>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-4">
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs px-2 py-1 rounded bg-secondary-100 text-secondary-700 whitespace-nowrap">
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="py-8 md:py-12">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            {/* Featured Image */}
            <div className="aspect-[16/9] rounded-lg overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
                title={`${article.title} - 无翼鸟漫画`}
              />
            </div>

            {/* Content */}
            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags bottom */}
            <div className="mt-10 pt-6 border-t border-background-200/70 flex flex-wrap items-center gap-3">
              <span className="text-sm text-foreground-600 font-medium">标签：</span>
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-12 md:py-16 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8">相关推荐</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-500/20 transition-all duration-200"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={rel.image}
                        alt={rel.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-foreground-950 mb-2 line-clamp-2 text-sm md:text-base group-hover:text-primary-500 transition-colors">
                        {rel.title}
                      </h3>
                      <div className="flex items-center gap-3 text-xs text-foreground-500">
                        <span className="whitespace-nowrap">{rel.date}</span>
                        <span className="whitespace-nowrap">{rel.views.toLocaleString()} 阅读</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <Footer />
      </div>
    </>
  );
}